'use client'

import { isNull } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { ReactNode, RefObject, useEffect, useRef, useState } from 'react'

import { useViewingRoomStore } from '@/entities/map'
import { cn, eventManager, useScrollTopDetector } from '@/shared/lib'
import { Icon } from '@/shared/ui'
import { MobileFilterMenuList } from '@/widgets/map-search-filter'

import {
  BottomSheet as BottomSheetPrimitive,
  type BottomSheetHandle,
  type Props as BottomSheetProps,
} from './bottom-sheet'
import { CollapseButton } from './collapse-button'
import { MOBILE_LAYER } from '../../model/layer'

type Props = Pick<BottomSheetProps, 'onCollapsedChange'> & {
  mapInitialized: boolean
  children: (props: {
    ref: RefObject<HTMLUListElement | null>
    onScroll: () => void
  }) => ReactNode
}

export const BottomSheet = ({ mapInitialized, children }: Props) => {
  const t = useTranslations('map')

  const filterRef = useRef<HTMLDivElement | null>(null)
  const sheetRef = useRef<BottomSheetHandle | null>(null)

  const [showMapButton, setShowMapButton] = useState(false)

  const viewingRoom = useViewingRoomStore(state => state.viewingRoom)

  const {
    ref: listRef,
    isScrolled,
    onScroll: handleContentScroll,
  } = useScrollTopDetector<HTMLUListElement>()

  const collapse = () => {
    sheetRef.current?.collapse()
  }

  const handleDragStart = () => {
    collapse()
  }

  const handleMapButtonClick = () => {
    collapse()
  }

  const handleBottomSheetCollapsedChange = (collapsed: boolean) => {
    setShowMapButton(!collapsed)
  }

  useEffect(() => {
    if (!mapInitialized) {
      return
    }

    eventManager.addEventListener('dragstart', handleDragStart)
  }, [mapInitialized])

  useEffect(() => {
    if (isNull(viewingRoom)) return

    sheetRef.current?.snapToDefaultHeight()
  }, [viewingRoom])

  return (
    <>
      <BottomSheetPrimitive
        ref={sheetRef}
        defaultCollapsed
        defaultCollapsedHeight={245}
        collapsedHeight={106}
        contentRef={listRef}
        filterRef={filterRef}
        style={{ zIndex: MOBILE_LAYER.ROOM_LIST }}
        className="h-[calc(100svh-146px)] max-h-[calc(100svh-146px)]"
        onCollapsedChange={handleBottomSheetCollapsedChange}
      >
        <div className="flex flex-1 flex-col overflow-hidden">
          <header className="sr-only">
            <h2 className="hidden">{t('mobile_header.title')}</h2>
            <p className="hidden">{t('mobile_header.description')}</p>
          </header>

          <div className="flex h-full flex-col gap-2">
            <MobileFilterMenuList ref={filterRef} isListScrolled={isScrolled} />
            <div className="flex min-h-0 flex-1 flex-col">
              {children({ ref: listRef, onScroll: handleContentScroll })}
            </div>
          </div>
        </div>
      </BottomSheetPrimitive>

      <div
        className={cn(
          'fixed bottom-[66px] left-1/2 -translate-x-1/2',
          'pointer-events-none opacity-0 transition-opacity duration-300',
          showMapButton && 'pointer-events-auto opacity-100',
        )}
        style={{ zIndex: MOBILE_LAYER.MAP_BUTTON }}
      >
        <CollapseButton onClick={handleMapButtonClick}>
          <Icon name="map" size={16} />
          {t('list.button.view_map')}
        </CollapseButton>
      </div>
    </>
  )
}
